import { useCallback, useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client/react";

import {
  DashboardSettingsDocument,
  PushSubscriptionBuildDocument,
} from "@app_schema";

type PermissionState = "default" | "granted" | "denied" | "unsupported";

export const usePushNotifications = () => {
  const [permissionState, setPermissionState] =
    useState<PermissionState>("default");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, refetch } = useQuery(DashboardSettingsDocument);
  const [subscribe] = useMutation(PushSubscriptionBuildDocument);

  const vapidPublicKey = data?.vapidPublicKey;
  const hasPushSubscriptions = data?.me?.hasPushSubscriptions ?? false;
  const supported = "Notification" in window && "serviceWorker" in navigator;

  useEffect(() => {
    if (!supported) {
      setPermissionState("unsupported");
      return;
    }

    setPermissionState(Notification.permission as PermissionState);
  }, [supported]);

  const enable = useCallback(async () => {
    console.log("[Push] Starting enable flow", { vapidPublicKey, supported });

    if (!vapidPublicKey) {
      setError("Push notifications not configured");
      return;
    }

    if (!supported) {
      setError("Push notifications not supported in this browser");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log("[Push] Requesting permission...");
      const permission = await Notification.requestPermission();
      console.log("[Push] Permission result:", permission);
      setPermissionState(permission as PermissionState);

      if (permission !== "granted") {
        setLoading(false);
        return;
      }

      console.log("[Push] Registering service worker...");
      await navigator.serviceWorker.register("/service-worker.js");
      const registration = await navigator.serviceWorker.ready;
      console.log("[Push] Service worker ready");

      console.log("[Push] Subscribing to push manager...");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
      console.log("[Push] Push subscription created:", subscription.endpoint);

      const keys = subscription.toJSON().keys;
      if (!keys?.p256dh || !keys?.auth) {
        throw new Error("Failed to get subscription keys");
      }

      console.log("[Push] Sending subscription to server...");
      const result = await subscribe({
        variables: {
          input: {
            endpoint: subscription.endpoint,
            p256dhKey: keys.p256dh,
            authKey: keys.auth,
          },
        },
      });

      const buildResult = result.data?.me?.pushSubscriptions?.build;
      console.log("[Push] Server response:", buildResult);

      if (buildResult?.status !== "OK") {
        const errorMessages = Object.entries(buildResult?.errors ?? {})
          .filter(([, v]) => v)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ");
        throw new Error(errorMessages || "Failed to save subscription");
      }

      console.log("[Push] Subscription saved successfully");
      await refetch();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to enable notifications";
      setError(message);
      console.error("[Push] Subscription failed:", e);
    } finally {
      setLoading(false);
    }
  }, [vapidPublicKey, supported, subscribe, refetch]);

  const enabled = permissionState === "granted" && hasPushSubscriptions;

  return {
    permissionState,
    loading,
    error,
    enable,
    supported,
    enabled,
  } as const;
};

const urlBase64ToUint8Array = (base64: string): Uint8Array<ArrayBuffer> => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const array = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array;
};

export const usePushSubscriptionSync = () => {
  const { data, refetch } = useQuery(DashboardSettingsDocument);
  const [subscribe] = useMutation(PushSubscriptionBuildDocument);

  const vapidPublicKey = data?.vapidPublicKey;
  const supported = "Notification" in window && "serviceWorker" in navigator;

  useEffect(() => {
    if (!supported || !vapidPublicKey) return;
    if (Notification.permission !== "granted") return;

    let cancelled = false;

    const syncSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (!subscription || cancelled) return;

        const keys = subscription.toJSON().keys;
        if (!keys?.p256dh || !keys?.auth) return;

        console.log("[Push] Auto-syncing subscription:", subscription.endpoint);

        await subscribe({
          variables: {
            input: {
              endpoint: subscription.endpoint,
              p256dhKey: keys.p256dh,
              authKey: keys.auth,
            },
          },
        });

        if (!cancelled) {
          await refetch();
          console.log("[Push] Auto-sync complete");
        }
      } catch (e) {
        if (!cancelled) console.error("[Push] Auto-sync failed:", e);
      }
    };

    syncSubscription();
    return () => {
      cancelled = true;
    };
  }, [supported, vapidPublicKey, subscribe]);
};
